/**
 * Custom usergroup icon for the Squad nav item.
 * Lucide-compatible: accepts size, color, strokeWidth props.
 * @module features/squad-planning/icons/UsergroupIcon
 */

import React, { memo } from "react";
import Svg, { Path } from "react-native-svg";

/** Default size matching lucide icons */
const DEFAULT_SIZE = 24;

/** Props matching the lucide icon interface */
interface UsergroupIconProps {
  /** Icon size in pixels */
  readonly size?: number;
  /** Stroke color */
  readonly color?: string;
  /** Stroke color (alias for color, used by GlueStack Icon) */
  readonly stroke?: string;
  /** Stroke width */
  readonly strokeWidth?: number;
}

/**
 * Usergroup SVG icon matching the Figma design for the Squad sidebar item.
 * @param props - Icon props
 * @param props.size - Icon size in pixels (default 24)
 * @param props.color - Stroke color
 * @param props.stroke - Stroke color alias
 * @param props.strokeWidth - Stroke width (default 1.33)
 */
const UsergroupIcon = ({
  size = DEFAULT_SIZE,
  color,
  stroke,
  strokeWidth = 1.33,
  ...rest
}: UsergroupIconProps) => (
  <Svg width={size} height={size} viewBox="0 0 20 20" fill="none" {...rest}>
    <Path
      d="M5.83331 3.33334C4.94926 3.33334 4.10141 3.68453 3.47629 4.30965C2.85117 4.93478 2.49998 5.78262 2.49998 6.66668C2.49998 7.55073 2.85117 8.39858 3.47629 9.0237C4.10141 9.64882 4.94926 10 5.83331 10C4.50723 10 3.23546 10.5268 2.29778 11.4645C1.3601 12.4022 0.833313 13.6739 0.833313 15V17.5M19.1666 17.5V15C19.1666 13.6739 18.6399 12.4022 17.7022 11.4645C16.7645 10.5268 15.4927 10 14.1666 10C15.0507 10 15.8985 9.64882 16.5237 9.0237C17.1488 8.39858 17.5 7.55073 17.5 6.66668C17.5 5.78262 17.1488 4.93478 16.5237 4.30965C15.8985 3.68453 15.0507 3.33334 14.1666 3.33334M13.3333 6.66668C13.3333 7.55073 12.9821 8.39858 12.357 9.0237C11.7319 9.64882 10.884 10 9.99998 10C9.11593 10 8.26808 9.64882 7.64296 9.0237C7.01784 8.39858 6.66665 7.55073 6.66665 6.66668C6.66665 5.78262 7.01784 4.93478 7.64296 4.30965C8.26808 3.68453 9.11593 3.33334 9.99998 3.33334C10.884 3.33334 11.7319 3.68453 12.357 4.30965C12.9821 4.93478 13.3333 5.78262 13.3333 6.66668ZM4.16665 15.8333C4.16665 14.9493 4.51784 14.1014 5.14296 13.4763C5.76808 12.8512 6.61592 12.5 7.49998 12.5H12.5C13.384 12.5 14.2319 12.8512 14.857 13.4763C15.4821 14.1014 15.8333 14.9493 15.8333 15.8333V17.5H4.16665V15.8333Z"
      stroke={color ?? stroke ?? "currentColor"}
      strokeWidth={strokeWidth}
      strokeLinecap="square"
    />
  </Svg>
);

UsergroupIcon.displayName = "UsergroupIcon";

export default memo(UsergroupIcon);
